import { computed, ref } from 'vue'
import { AxiosResponse } from 'axios'
import { accountManagementApi, approvalsApi, dataAssetsApi } from '@/api'
import {
  DataObjectDTO,
  DataObjectRequest,
  DataObjectData,
  DataObjectReference,
  DataObjectInfo,
  DataObjectStatus,
  ReferenceType,
  PropItems,
  CanonicalAction,
  DataObjectReferenceDto,
} from '@coac-gmbh/saifty-main-apis'
import { useI18n } from 'vue-i18n'
import useToasts from './useToasts'
import {
  DEFAULT_FLOW_TYPE,
  TECHNICAL_DATA_SHEET_FLOW_TYPE,
  SDS_CANONICAL_NAME,
  TDS_CANONICAL_NAME,
  hideStoreToSAPPage,
  SAIFTY_NOTES_SUFFIX,
  SAIFTY_STATUS_SUFFIX,
  COMPUTED_SAIFTY_CHECKS_KEYWORD,
  SDS_CANONICAL_PRODUCT_NUMBER,
  FlowWithData,
  SDS_CANONICAL_RECORD_NUMBER,
  SDS_CANONICAL_CAS,
  SDS_CANONICAL_EG_NO,
  DataObjectSource,
  SDS_CANONICAL_LAST_CHANGE_ON,
  SDS_CANONICAL_LAST_CHANGE_BY,
  SDS_CANONICAL_CREATED_BY,
  SDS_CANONICAL_DATE_OF_UPLOAD,
  SAPReferenceTitle,
  DocumentSourceType,
} from '@/config'
import { DataObjectItem, PhrasePointObject } from '@coac-gmbh/saifty-phrases'
import { PropertyArray } from '@coac-gmbh/saifty-sap'
import dayjs from 'dayjs'
import { useAuth } from '@/auth'
import {
  ActivityActions,
  ActivityTypes,
  ApprovalActivityLog,
  MaterialActivityLog,
  SapActivityLog,
} from '@/composables/useActivityLogs'
import { TableCompositionGroup } from '@/composables/useSAPComparisons'
import { IS_BAYER, IS_LANXESS } from '@/config/customers'
import { useSAPConfig } from '@/composables/useSAPConfig'
import useSearchTags, { ToggleSearchItem } from './useSearchTags'
import { ApprovalReference, ApprovalReviewer } from './useDocuments'
import utc from 'dayjs/plugin/utc.js'

interface DataObjectRequestWithMetadata extends DataObjectRequest {
  metaData: DataObjectMetadata
  id: string
  dataAssetsId: string
}

export interface SAPDataObjectCached {
  metadata: {
    props?: DataObjectItem[]
    compositions?: TableCompositionGroup[]
  }
}

export interface PropItemsFromSharedInstance extends PropItems {
  destinationPhraseKey?: string
}

export interface DataObjectFile {
  documentId: string
  fileName: string
  fileCommonIdentifier?: string
}

export interface PublicProductPassportDocument extends DataObjectFile {
  labelImageCommonIdentifier?: string
}

export interface SapDocuments {
  recn: string
  documentId: string
  dataObjectId: string
  dataAssetId: string
  fileName: string
  instanceId: string
  reportType: string
  version: string
  subVersion: string
  langu: string
  createdDate: string
  updatedDate: string
}

export interface DataObjectMetadata {
  phrases?: PhrasePointObject[]
  compositionsReferences?: CompositionReference[]
  hazardInducerReferences?: HazardInducerReference[]
  inheritanceReference?: InheritanceReference
  materialActivityLog?: MaterialActivityLog[]
  SAPActivityLog?: SapActivityLog[]
  cachedDataForStoreToSAP?: SAPDataObjectCached
  markedForAITraining?: boolean
  languageCode?: string // We use it to save the language code in the dataObject in specific features, for example in case the material be shared and the user's permissions cannot access to the approval language code
  region?: string
  businessUnit?: string
  activityLog?: ApprovalActivityLog[]
  missingCanonicalsAlreadyCreated?: boolean
  decimalNotationFormatAlreadyExecuted?: boolean
  sections?: { key: string; page: number }[]
  dataObjectToUpdateReference?: ApprovalReference
  sap_differences?: {
    spec_id: string[]
    cannonicals: string[]
  }
  sds_differences?: {
    sections: any
    cannonicals: string[]
  }
  cannonical_differences?: {
    sections: any
    cannonicals: string[]
  }
  storedInSAP?: boolean
  externalId?: string
  migratedToNewDataModel?: boolean
  [key: string]: any
  firstSAPReferenceLoaded?: boolean
  dataFromOriginalInstance?: DataObjectData[]
  publicProductPassportDocument?: PublicProductPassportDocument[]
  passportId?: string // This passport will be selected by default and will take priority over the extraction passport
  dataObjectFiles?: DataObjectFile[]
  sapDocumentIds?: SapDocuments[]
}

export interface DataObjectActions {
  newItems?: Array<DataObjectData>
  newCompositionReferences?: Array<CompositionReference>
  deleteCompositionReferences?: Array<CompositionReference>
  overwritePhrases?: PhrasePointObject[]
  newStatus?: DataObjectStatus
}

export interface ReferenceMetadata {
  recordNoofSource?: string
  recordNoOfTemplHeader?: string
  refSubsRecordNo?: string
  relInd?: boolean
  [key: string]: any
}

// A composition reference could be saved with a CasNo or egNr, never both
export interface CompositionReference extends DataObjectReference {
  casNo: string
  egNr: string
  substanceId?: string
  recordNo?: string
}

// Metadata stored by the backend in the object references (typed as 'any' in the API models)
export interface ComponentReferenceMetadata {
  component_index?: number
}

export interface InheritanceReference extends DataObjectReference {
  recordNoOfTemplHeader: string
  recordNoofSource: string
}

export interface HazardInducerReference extends DataObjectReference {
  dangodreg: string // Value I.e. 'ADNR'
  recordNoOfSub: string // Record Number
}

export interface CompositionReferenceWithName extends CompositionReference {
  name: string
}

export const CERTIFICATES_DATA_ASSET_ID = window.ENV.CERTIFICATES_DATA_ASSET_ID
  ? window.ENV.CERTIFICATES_DATA_ASSET_ID
  : import.meta.env.VITE_CERTIFICATES_DATA_ASSET_ID

export function findObjectField(objectData: DataObjectData[] | Readonly<DataObjectData[]>, fieldName: string) {
  return objectData.find((item) => item.fieldName.toLowerCase() === fieldName.toLowerCase())
}

export function findObjectFieldBySuffixName(
  objectData: DataObjectData[] | Readonly<DataObjectData[]>,
  fieldName: string,
) {
  let field = objectData.find((item) => item.fieldName?.toLowerCase().split('.')[1] === fieldName.toLowerCase())
  if (!field) {
    // Fallback to full field name check
    field = objectData.find((item) => item.fieldName.toLowerCase() === fieldName.toLowerCase())
  }
  return field
}

export function findObjectName(objectData: DataObjectData[] | Readonly<DataObjectData[]>) {
  return findObjectField(objectData, SDS_CANONICAL_NAME)
}

export function findObjectProductNumber(objectData: DataObjectData[] | Readonly<DataObjectData[]>) {
  return findObjectField(objectData, SDS_CANONICAL_PRODUCT_NUMBER)
}

export function findObjectRecordNumber(objectData: DataObjectData[] | Readonly<DataObjectData[]>) {
  return findObjectField(objectData, SDS_CANONICAL_RECORD_NUMBER)
}

export function findObjectCASNumber(objectData: DataObjectData[] | Readonly<DataObjectData[]>) {
  return findObjectField(objectData, SDS_CANONICAL_CAS)
}

export function findObjectEGNumber(objectData: DataObjectData[] | Readonly<DataObjectData[]>) {
  return findObjectField(objectData, SDS_CANONICAL_EG_NO)
}

export function isDataObjectCertificateType(dataObject: DataObjectDTO) {
  return !!CERTIFICATES_DATA_ASSET_ID && dataObject.dataAssetsId === CERTIFICATES_DATA_ASSET_ID
}

export default function useDataObjects({
  extraction = undefined,
}: {
  extraction?: FlowWithData
} = {}) {
  const { t } = useI18n()
  const auth = useAuth()
  const { addToast } = useToasts()
  const { getConfigByBusinessUnit } = useSAPConfig()
  const { PRODUCT_NUMBER_TAG } = useSearchTags()
  const dataObjectUpdated = ref<DataObjectDTO>()
  const deletingItem = ref()
  const addOrRemovingHazardInducerReference = ref(false)
  const removingReferences = ref(false)
  const referenceTypeListToEnableSelectMultiple = [
    ReferenceType.Component.toString(),
    ReferenceType.Material.toString(),
  ]

  const referencesOptions = computed(() => {
    return [
      { name: t('Component'), value: ReferenceType.Component },
      { name: t('Material reference'), value: ReferenceType.Material },
      { name: SAPReferenceTitle, value: ReferenceType.Sap },
      { name: t('Inheritance'), value: ReferenceType.Inheritance },
      { name: t('Spec reference'), value: ReferenceType.Refsub },
      { name: t('Previous version'), value: ReferenceType.PreviousVersion },
    ].filter((i) => {
      if (hideStoreToSAPPage && i.value === ReferenceType.Sap) {
        return false
      } else if (!window.ENV.INHERITANCE_REFERENCES_ENABLED && i.value === ReferenceType.Inheritance) {
        return false
      } else if (!window.ENV.MATERIAL_REFERENCES_ENABLED && i.value === ReferenceType.Material) {
        return false
      } else if (!window.ENV.SPEC_REFERENCES_ENABLED && i.value === ReferenceType.Refsub) {
        return false
      }

      return true
    })
  })

  const inheritanceOptions = computed(() => {
    // Items separated by comma
    return (
      getConfigByBusinessUnit(extraction?.data.validate.businessUnit)?.sap_templates_options ||
      window.ENV.SAP_TEMPLATES_OPTIONS ||
      ''
    )
      ?.split(',')
      .map((s) => s.trim())
      .map((opt) => {
        // Option name and ID separated by dash. E.g: <Name 1>-<ID 1>, second-option-<ID 2>. We always take the value after the last dash as the 'id'
        const optionName = opt.substring(0, opt.lastIndexOf('-'))
        const optionValue = opt.split('-').pop() || ''

        return { name: optionName, value: optionValue }
      })
  })

  // For now the fields to show in the material activity log are the same, so, we use the same constant for now
  const materialFieldsToShowInActivityLog = [
    {
      key: 'fieldName',
      label: t('Name'),
      canBeTranslated: true,
      isFieldToShowDetails: true,
    },
    {
      key: 'fieldValue',
      label: t('Value'),
    },
  ]
  const materialActivityFieldsToShowByAction = [
    {
      action: ActivityActions.Create,
      itemsToShowDifferences: materialFieldsToShowInActivityLog,
    },
    {
      action: ActivityActions.Update,
      itemsToShowDifferences: materialFieldsToShowInActivityLog,
    },
    {
      action: ActivityActions.Delete,
      itemsToShowDifferences: materialFieldsToShowInActivityLog,
    },
  ]

  async function updateDataObject(
    dataObject: DataObjectDTO,
    action: DataObjectActions,
    callMetadataEndpoint?: boolean,
    source: DataObjectSource = 'dataobject',
  ): Promise<AxiosResponse<DataObjectInfo>> {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({
        text: t('The material could not be updated because the material has no object ID or asset ID'),
        variant: 'warning',
      })
      return Promise.reject()
    }

    const existingData = dataObject.data || []
    const newItemList = new Map((action.newItems || []).map((item) => [item.fieldName, item]))

    const updatedDataItems = existingData.map((oldItem) => {
      if (!oldItem.fieldName) return oldItem

      const replacement = newItemList.get(oldItem.fieldName)
      if (replacement) {
        newItemList.delete(oldItem.fieldName)
        return replacement
      }

      return oldItem
    })

    const newItemsToAdd = Array.from(newItemList.values())

    const newDataObject: DataObjectRequestWithMetadata = {
      ...dataObject,
      data: [...updatedDataItems, ...newItemsToAdd],
      metaData: (dataObject.metaData || {}) as DataObjectMetadata,
    }

    if (action.overwritePhrases) {
      newDataObject.metaData.phrases = action.overwritePhrases
    }

    // Remove composition references
    if (action.deleteCompositionReferences) {
      newDataObject.metaData = {
        ...newDataObject.metaData,
        compositionsReferences: (newDataObject.metaData.compositionsReferences || []).filter(
          (compositionRef) =>
            !action.deleteCompositionReferences?.find((referenceToRemove) => {
              if (referenceToRemove.recordNo) {
                return referenceToRemove.recordNo === compositionRef.recordNo
              } else if (referenceToRemove.casNo) {
                return referenceToRemove.casNo === compositionRef.casNo
              } else if (referenceToRemove.egNr) {
                return referenceToRemove.egNr === compositionRef.egNr
              }
            }),
        ),
      }

      action.deleteCompositionReferences.forEach((compositionReferenceToRemove) => {
        const compositionReferencesToTheSameMaterial = (newDataObject.metaData.compositionsReferences || []).filter(
          (compositionReference) => compositionReference.dataObjectId === compositionReferenceToRemove.dataObjectId,
        )

        // Currently, when the user removes some composition reference, we want to remove it to the main references, only when it don't exist a composition reference to a specific material
        if (!compositionReferencesToTheSameMaterial || compositionReferencesToTheSameMaterial.length === 0) {
          // In order to avoid side effects, we filter the sapReferences and save it in a constant, then, we manipulate
          // separately the another references, in order to remove only the references with 'Component' or 'null' referenceType
          const sapReferences =
            newDataObject.references?.filter((mainReference) => mainReference.referenceType === ReferenceType.Sap) || []
          newDataObject.references = [
            ...sapReferences,
            ...(newDataObject.references?.filter(
              (mainReference) =>
                mainReference.dataObjectId !== compositionReferenceToRemove.dataObjectId &&
                mainReference.referenceType !== ReferenceType.Sap,
            ) || []),
          ]
        }
      })
    }

    // Add composition references
    if (action.newCompositionReferences) {
      newDataObject.metaData = {
        ...newDataObject.metaData,
        compositionsReferences: [
          ...(newDataObject.metaData?.compositionsReferences || []),
          ...action.newCompositionReferences,
        ],
      }

      action.newCompositionReferences.forEach((newCompositionReference) => {
        const mainReferenceAlreadyExists = newDataObject.references?.some(
          (mainReference) =>
            mainReference.dataAssetId === newCompositionReference.dataAssetId &&
            mainReference.dataObjectId === newCompositionReference.dataObjectId &&
            mainReference.referenceType === newCompositionReference.referenceType,
        )
        // Currently, when the user adds some composition reference, we want to add it to the main references, but we validate it to don't add duplicates
        if (!mainReferenceAlreadyExists)
          newDataObject.references = [...(newDataObject.references || []), newCompositionReference]
      })
    }

    try {
      // We use this trick to send all fields of the new data object except the metadata field, so that if a new field is added in the future, it will be sent, while avoiding sending the metadata to make the payload lighter
      const { metaData, ...newDataObjectRemainingFields } = newDataObject
      const response = await dataAssetsApi.updateDataAssetsObject({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        dataObjectChange: newDataObjectRemainingFields,
        source,
      })
      dataObject.checksum = response.data.checksum

      if (callMetadataEndpoint) {
        // We need to update the metadata to save the composition and inheritance references
        await dataAssetsApi.replaceDataObjectMetaData({
          dataAssetId: dataObject.dataAssetsId,
          dataObjectId: dataObject.id,
          body: newDataObject.metaData,
        })
      }

      dataObjectUpdated.value = {
        ...newDataObject,
        checksum: response.data.checksum,
      }

      return response
    } catch (e) {
      console.log(e)
      return Promise.reject(e)
    }
  }

  async function updateDataObjectMetadata(dataObject: DataObjectDTO, newMetadata: DataObjectMetadata) {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({
        text: t('The metadata of the material could not be updated because the material has no object ID or asset ID'),
        variant: 'warning',
      })
      return Promise.reject()
    }

    const metadata: DataObjectMetadata = {
      ...dataObject.metaData,
      ...newMetadata,
    }

    try {
      await dataAssetsApi.replaceDataObjectMetaData({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        body: metadata,
      })
      dataObjectUpdated.value = {
        ...dataObject,
        metaData: metadata,
      }
      // We return the object value updated because the endpoint function returns 'void'
      return dataObjectUpdated.value
    } catch (e) {
      return Promise.reject(e)
    }
  }

  async function addHazardInducerReference(dataObject: DataObjectDTO, hazIndReference: HazardInducerReference) {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({
        text: t('The metadata of the material could not be updated because the material has no object ID or asset ID'),
        variant: 'warning',
      })
      return Promise.reject()
    }

    const metadata: DataObjectMetadata = {
      ...dataObject.metaData,
    }

    metadata.hazardInducerReferences = [...(metadata.hazardInducerReferences || []), hazIndReference]

    addOrRemovingHazardInducerReference.value = true
    try {
      await dataAssetsApi.replaceDataObjectMetaData({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        body: metadata,
      })
      dataObjectUpdated.value = {
        ...dataObject,
        metaData: metadata,
      }

      addOrRemovingHazardInducerReference.value = false
      // We return the object value updated because the endpoint function returns 'void'
      return dataObjectUpdated.value
    } catch (e) {
      addOrRemovingHazardInducerReference.value = false
      return Promise.reject(e)
    }
  }

  async function removeHazardInducerReference(
    dataObject: DataObjectDTO,
    hazIndReferenceToRemove: HazardInducerReference,
  ) {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({
        text: t('The metadata of the material could not be updated because the material has no object ID or asset ID'),
        variant: 'warning',
      })
      return Promise.reject()
    }

    const metadata: DataObjectMetadata = {
      ...dataObject.metaData,
    }

    metadata.hazardInducerReferences = metadata.hazardInducerReferences?.filter(
      (i) =>
        i.dataAssetId !== hazIndReferenceToRemove.dataAssetId &&
        i.dataObjectId !== hazIndReferenceToRemove.dataObjectId &&
        i.dangodreg !== hazIndReferenceToRemove.dangodreg &&
        i.recordNoOfSub !== hazIndReferenceToRemove.recordNoOfSub,
    )

    addOrRemovingHazardInducerReference.value = true
    try {
      await dataAssetsApi.replaceDataObjectMetaData({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        body: metadata,
      })
      dataObjectUpdated.value = {
        ...dataObject,
        metaData: metadata,
      }

      addOrRemovingHazardInducerReference.value = false
      // We return the object value updated because the endpoint function returns 'void'
      return dataObjectUpdated.value
    } catch (e) {
      addOrRemovingHazardInducerReference.value = false
      return Promise.reject(e)
    }
  }

  async function addDataObjectReferences(
    dataObject: DataObjectDTO,
    referencesToAdd: (DataObjectReference & DataObjectReferenceDto)[],
    source: DataObjectSource = 'dataobject',
    shouldUpdateDataObject = true,
  ) {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({
        text: t('The metadata of the material could not be updated because the material has no object ID or asset ID'),
        variant: 'warning',
      })
      return Promise.reject()
    }

    try {
      // 'Unitary' references are references that can only exist as a single type of this reference
      const newUnitaryReferenceFound = referencesToAdd.find(
        (reference) =>
          (!window.ENV.MULTIPLE_INHERITANCE_REFERENCES_ENABLED &&
            reference.referenceType === ReferenceType.Inheritance) ||
          (reference.referenceType === ReferenceType.Sap && !window.ENV.ENABLE_MULTIPLE_SAP_REFERENCES),
      )

      if (newUnitaryReferenceFound) {
        const previousUnitaryReference = dataObject.references?.find(
          (reference) => reference.referenceType === newUnitaryReferenceFound.referenceType,
        )

        if (previousUnitaryReference) {
          await removeDataObjectReferences(dataObject, [previousUnitaryReference])
        }
      }

      const { data } = await dataAssetsApi.addDataAssetsObjectReference({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        dataObjectReferenceChange: {
          references: referencesToAdd,
        },
      })
      if (source === 'dataobject') dataObject.checksum = data.checksum // We need to ignore the checksum when it is approval because this checksum should not change

      const materialReferences = referencesToAdd.filter(
        (reference) => reference.referenceType === ReferenceType.Material,
      )

      // The back reference should only be added for "Material" references, and the back Reference type would be "Specification"
      if (materialReferences.length > 0) {
        materialReferences.forEach(async (materialReference) => {
          if (!materialReference.dataObjectId) return

          // Load the material to get the references
          const { data } = await dataAssetsApi.getDataAssetsObject({
            dataAssetId: materialReference.dataAssetId,
            dataObjectId: materialReference.dataObjectId,
            source: 'dataobject',
          })

          // Find if the material already has the specification reference
          const specificationReferenceFound = data.references?.find(
            (reference) =>
              reference.dataAssetId === dataObject.dataAssetsId &&
              reference.dataObjectId === dataObject.id &&
              reference.referenceType === ReferenceType.Specification,
          )

          if (!specificationReferenceFound && dataObject.dataAssetsId && dataObject.id) {
            addDataObjectReferences(
              data,
              [
                {
                  dataAssetId: dataObject.dataAssetsId,
                  dataObjectId: dataObject.id,
                  referenceType: ReferenceType.Specification,
                },
              ],
              source,
              false,
            )
          }
        })
      }

      let dataObjectResponseToReturn = dataObject
      if (shouldUpdateDataObject) {
        dataObjectUpdated.value = {
          ...dataObject,
          checksum: source === 'dataobject' ? data.checksum : dataObject.checksum, // We need to ignore the checksum when it is approval because this checksum should not change
          references: getReferencesWithName(
            [...(dataObject.references || []), ...(referencesToAdd || [])],
            data.references || [],
          ),
        }

        dataObjectResponseToReturn = dataObjectUpdated.value
      }

      return dataObjectResponseToReturn
    } catch (e) {
      return Promise.reject(e)
    }
  }

  async function removeDataObjectReferences(
    dataObject: DataObjectDTO,
    referencesToRemove: DataObjectReference[],
    source: DataObjectSource = 'dataobject',
    shouldUpdateDataObject = true,
  ) {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({
        text: t('The metadata of the material could not be updated because the material has no object ID or asset ID'),
        variant: 'warning',
      })
      return Promise.reject()
    }

    removingReferences.value = true
    try {
      const { data } = await dataAssetsApi.removeDataAssetsObjectReference({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        dataObjectReferenceChange: {
          references: referencesToRemove,
        },
      })

      if (source === 'dataobject') dataObject.checksum = data.checksum // We need to ignore the checksum when it is approval because this checksum should not change

      const componentReferences = referencesToRemove.filter(
        (reference) => reference.referenceType === ReferenceType.Component,
      )

      const dataObjectMetadata = dataObject?.metaData as DataObjectMetadata | undefined
      const compositionReferencesToDelete = dataObjectMetadata?.compositionsReferences?.filter((compositionReference) =>
        componentReferences.find(
          (componentReference) =>
            componentReference.dataAssetId === compositionReference.dataAssetId &&
            componentReference.dataObjectId === compositionReference.dataObjectId &&
            componentReference.referenceType === compositionReference.referenceType,
        ),
      )

      if (compositionReferencesToDelete && compositionReferencesToDelete.length > 0) {
        await updateDataObject(dataObject, { deleteCompositionReferences: compositionReferencesToDelete }, true)
        if (dataObjectUpdated.value) dataObject.metaData = dataObjectUpdated.value.metaData
      }

      const materialReferences = referencesToRemove.filter(
        (reference) => reference.referenceType === ReferenceType.Material,
      )

      // We have a logic to add a back 'Specification' reference when the user adds a 'Material' reference. So, we need to do the same on deletion
      if (materialReferences.length > 0) {
        materialReferences.forEach(async (materialReference) => {
          if (!materialReference.dataObjectId) return

          // Load the material to get the references
          const { data } = await dataAssetsApi.getDataAssetsObject({
            dataAssetId: materialReference.dataAssetId,
            dataObjectId: materialReference.dataObjectId,
            source: 'dataobject',
          })

          // Find if the material already has the specification reference
          const specificationReferenceFound = data.references?.find(
            (reference) =>
              reference.dataAssetId === dataObject.dataAssetsId &&
              reference.dataObjectId === dataObject.id &&
              reference.referenceType === ReferenceType.Specification,
          )

          if (specificationReferenceFound && dataObject.dataAssetsId && dataObject.id) {
            removeDataObjectReferences(
              data,
              [
                {
                  dataAssetId: dataObject.dataAssetsId,
                  dataObjectId: dataObject.id,
                  referenceType: ReferenceType.Specification,
                },
              ],
              source,
              false,
            )
          }
        })
      }

      let dataObjectResponseToReturn = dataObject

      if (shouldUpdateDataObject) {
        dataObjectUpdated.value = {
          ...dataObject,
          checksum: source === 'dataobject' ? data.checksum : dataObject.checksum, // We need to ignore the checksum when it is approval because this checksum should not change
          references: getReferencesWithName([...(dataObject.references || [])], data.references || []),
        }

        dataObjectResponseToReturn = dataObjectUpdated.value
      }

      removingReferences.value = false
      return dataObjectResponseToReturn
    } catch (e) {
      removingReferences.value = false
      return Promise.reject(e)
    }
  }

  function getReferencesWithName(oldReferences: DataObjectReferenceDto[], newReferences: DataObjectReference[]) {
    function getReferenceKey(reference: DataObjectReferenceDto | DataObjectReference) {
      return `${reference.dataAssetId}:${reference.dataObjectId}`
    }
    const referenceNames = new Map(oldReferences.map((reference) => [getReferenceKey(reference), reference.name]))

    const referencesWithName = newReferences.map((reference) => ({
      ...reference,
      name: referenceNames.get(getReferenceKey(reference)) || reference.dataObjectId,
    }))

    return referencesWithName
  }

  async function deleteItemFromDataObject(
    dataAssetId: string,
    dataObjectId: string,
    dataObject: DataObjectDTO,
    itemToDelete: DataObjectData,
  ) {
    const newDataObject: DataObjectDTO = {
      ...dataObject,
      data: dataObject.data?.filter((data) => {
        return data.fieldName !== itemToDelete.fieldName
      }),
    }

    try {
      deletingItem.value = true
      // We use this trick to send all fields of the new data object except the metadata field, so that if a new field is added in the future, it will be sent, while avoiding sending the metadata to make the payload lighter
      const { metaData, ...newDataObjectRemainingFields } = newDataObject
      const response = await dataAssetsApi.updateDataAssetsObject({
        dataAssetId: dataAssetId,
        dataObjectId: dataObjectId,
        dataObjectChange: {
          ...newDataObjectRemainingFields,
        },
        source: 'dataobject',
      })
      dataObject.checksum = response.data.checksum

      const metadata: DataObjectMetadata = {
        ...dataObject.metaData,
      }
      const materialMetadataToUpdate = {
        materialActivityLog: [
          ...(metadata?.materialActivityLog || []),
          ...formatMaterialActivityLog(ActivityActions.Delete, [{ object: itemToDelete }]),
        ],
      }
      await updateDataObjectMetadata(dataObject, materialMetadataToUpdate)

      addToast({ text: `${t('Item deleted')}!`, variant: 'success' })
      dataObject.data = [...(newDataObject.data || [])]
      dataObjectUpdated.value = {
        ...newDataObject,
        data: newDataObject.data,
        checksum: response.data.checksum,
        metaData: { ...newDataObject.metaData, ...materialMetadataToUpdate },
      }
      return response
    } catch {
      addToast({ text: t('Error deleting material item'), variant: 'danger' })
    } finally {
      deletingItem.value = false
    }
  }

  async function updateOrCreateItemFromDataObject(dataObject: DataObjectDTO, itemToUpdate: DataObjectData) {
    if (!dataObject.dataAssetsId || !dataObject.id) {
      addToast({ text: t('No data asset Id or data object Id provided'), variant: 'warning' })
      return
    }
    const hasItem = dataObject.data?.find((item) => item.fieldName === itemToUpdate.fieldName)

    let isCreating = true
    const preparedItemChanges = <{ object: DataObjectData; objectChanges?: DataObjectData }>{
      object: itemToUpdate,
      objectChanges: undefined,
    }

    let newData: DataObjectDTO
    if (hasItem) {
      newData = {
        ...dataObject,
        data: dataObject.data?.map((item) => {
          if (item.fieldName === itemToUpdate.fieldName) {
            preparedItemChanges.object = item
            return itemToUpdate
          } else {
            return item
          }
        }),
      }
      preparedItemChanges.objectChanges = itemToUpdate
      isCreating = false
    } else {
      newData = {
        ...dataObject,
        data: [...(dataObject.data || []), itemToUpdate],
      }
    }

    try {
      // We use this trick to send all fields of the new data object except the metadata field, so that if a new field is added in the future, it will be sent, while avoiding sending the metadata to make the payload lighter
      const { metaData, ...newDataObjectRemainingFields } = newData
      const response = await dataAssetsApi.updateDataAssetsObject({
        dataAssetId: dataObject.dataAssetsId,
        dataObjectId: dataObject.id,
        dataObjectChange: {
          ...newDataObjectRemainingFields,
        },
        source: 'dataobject',
      })
      dataObject.checksum = response.data.checksum
      dataObject = newData
      dataObjectUpdated.value = newData

      // *** Start logic to save material log
      const metadata: DataObjectMetadata = {
        ...dataObject.metaData,
      }
      const materialActivityLogsFormatted = [
        ...(metadata.materialActivityLog || []),
        ...formatMaterialActivityLog(isCreating ? ActivityActions.Create : ActivityActions.Update, [
          preparedItemChanges,
        ]),
      ]
      await updateDataObjectMetadata(dataObject, { materialActivityLog: materialActivityLogsFormatted })
      // *** End logic
      addToast({ text: `${t('Material item updated')}!`, variant: 'success' })
      return response
    } catch {
      addToast({ text: t('Error updating material item'), variant: 'danger' })
    } finally {
      deletingItem.value = false
    }
  }

  async function syncZeroSectionTrackingItems({
    pageSource,
    itemsToTrackChanges,
    dataObject,
  }: {
    pageSource: DocumentSourceType
    itemsToTrackChanges: DataObjectItem[]
    dataObject: DataObjectDTO
  }) {
    dayjs.extend(utc)
    const nowUTC = dayjs.utc().toISOString()

    if (itemsToTrackChanges.length) {
      try {
        itemsToTrackChanges.forEach((item) => {
          switch (item.fieldName!) {
            case SDS_CANONICAL_LAST_CHANGE_ON:
              item.fieldValue = dayjs(nowUTC).utc().format('YYYY-MM-DD')
              break
            case SDS_CANONICAL_LAST_CHANGE_BY:
              item.fieldValue = auth.user?.email || item.fieldValue || ''
              break
            case SDS_CANONICAL_CREATED_BY:
              if (!item.fieldValue && auth.user?.email) item.fieldValue = auth.user.email
              break
            case SDS_CANONICAL_DATE_OF_UPLOAD:
              item.fieldValue = dayjs(dataObject.createdDate || nowUTC).format('YYYY-MM-DD')
              break
          }
        })
        const itemsToUpdate = itemsToTrackChanges.map((item) => ({
          ...item,
          fieldName: item.fieldName || '',
          fieldValue: item.fieldValue || '',
          action: CanonicalAction.Verified,
        }))

        if (pageSource === 'approval') {
          await approvalsApi.createOrUpdateItemsInApproval({
            dataAssetId: dataObject.dataAssetsId!,
            dataObjectId: dataObject.id!,
            dataObjectData: itemsToUpdate,
          })
        } else if (pageSource === 'dataobject') {
          await updateDataObject(dataObject, { newItems: itemsToUpdate })
        }
      } catch (error) {
        addToast({ text: t('Error updating tracking items of zero section'), variant: 'danger' })
      }
    }
  }

  function getCanonicalDocumentName(extractionType: string) {
    switch (extractionType) {
      case DEFAULT_FLOW_TYPE:
        return SDS_CANONICAL_NAME
      case TECHNICAL_DATA_SHEET_FLOW_TYPE:
        return TDS_CANONICAL_NAME
      default:
        return SDS_CANONICAL_NAME // TODO: Define logic to return a custom canonical name setting by the user
    }
  }

  function formatMaterialActivityLog(
    action: ActivityActions,
    itemsChanges: { object: DataObjectData; objectChanges?: DataObjectData }[],
  ) {
    return itemsChanges.map<MaterialActivityLog>((item) => {
      return {
        time: dayjs().format('MM.DD.YYYY, HH:mm:ss'),
        user: IS_LANXESS
          ? auth.user?.id || auth.user?.nickname || auth.user?.email || 'N/A'
          : auth.user?.nickname || auth.user?.email || 'N/A',
        action,
        type: ActivityTypes.Material,
        object: item.object,
        objectChanges: item.objectChanges ? item.objectChanges : undefined,
      }
    })
  }

  function formatSapActivityLog(
    action: ActivityActions,
    itemsChanges: { object: PropertyArray[]; objectChanges?: PropertyArray[] }[],
    success?: boolean,
    errors?: string[],
  ) {
    return itemsChanges.map<SapActivityLog>((item) => {
      return {
        time: dayjs().format('MM.DD.YYYY, HH:mm:ss'),
        user: IS_LANXESS
          ? auth.user?.id || auth.user?.nickname || auth.user?.email || 'N/A'
          : auth.user?.nickname || auth.user?.email || 'N/A',
        action,
        type: ActivityTypes.SAP,
        object: item.object,
        objectChanges: item.objectChanges ? item.objectChanges : undefined,
        success,
        errors: success ? undefined : errors,
      }
    })
  }

  function getMaterialActivityFieldsToShowByAction(action: ActivityActions) {
    return materialActivityFieldsToShowByAction.find((a) => a.action === action)
  }

  function filterOutMetadataItems(data: DataObjectData[]) {
    return data.filter((item) => {
      return (
        !item.fieldName?.includes(SAIFTY_NOTES_SUFFIX) &&
        !item.fieldName?.includes(SAIFTY_STATUS_SUFFIX) &&
        !item.fieldName?.includes(COMPUTED_SAIFTY_CHECKS_KEYWORD) &&
        !item.fieldName?.startsWith('SEC_0')
      )
    })
  }

  function sortItems(items: DataObjectData[]) {
    // Sort data object items by section, e.g. SEC_1_1, SEC_1_2, SEC_2_1, etc.
    return items.sort((a, b) => {
      if (!a.fieldName || !b.fieldName) return 0
      const aSection = a.fieldName.split('_').length > 1 ? parseInt(a.fieldName.split('_')[1]) : 0
      const bSection = b.fieldName.split('_').length > 1 ? parseInt(b.fieldName.split('_')[1]) : 0
      const aSubSection = a.fieldName.split('_').length > 2 ? parseInt(a.fieldName.split('_')[2]) : 0
      const bSubSection = b.fieldName.split('_').length > 2 ? parseInt(b.fieldName.split('_')[2]) : 0
      if (aSection < bSection) return -1
      if (aSection > bSection) return 1
      if (aSubSection < bSubSection) return -1
      if (aSubSection > bSubSection) return 1

      return 0
    })
  }

  function getDefaultReferenceSearchTagsByReferenceType(referenceType: string) {
    let searchTagsToReturn: ToggleSearchItem[] = []
    const INHERITANCE_RELATED_SUBSTANCE_ID_PREFIX = window.ENV.INHERITANCE_RELATED_SUBSTANCE_ID_PREFIX

    if (referenceType === ReferenceType.Inheritance && INHERITANCE_RELATED_SUBSTANCE_ID_PREFIX) {
      searchTagsToReturn = [
        {
          tag: PRODUCT_NUMBER_TAG,
          value: INHERITANCE_RELATED_SUBSTANCE_ID_PREFIX,
          active: true,
          toggle: true,
        },
      ]
    }

    return searchTagsToReturn
  }

  function formatReferenceMetadata({
    referenceType,
    recordNumber,
    templateRecordNumber,
  }: {
    referenceType?: string
    recordNumber?: string
    templateRecordNumber?: string
  }) {
    if (window.ENV.INHERITANCE_REFERENCES_ENABLED && referenceType && referenceType === ReferenceType.Inheritance) {
      return {
        recordNoofSource: recordNumber,
        recordNoOfTemplHeader: templateRecordNumber,
      }
    }
    if (window.ENV.SPEC_REFERENCES_ENABLED && referenceType && referenceType === ReferenceType.Refsub) {
      return {
        refSubsRecordNo: recordNumber,
        relInd: window.ENV.SPEC_REFERENCE_REL_IND_VALUE,
      }
    }

    return undefined
  }

  return {
    inheritanceOptions,
    referencesOptions,
    deletingItem,
    dataObjectUpdated,
    addOrRemovingHazardInducerReference,
    referenceTypeListToEnableSelectMultiple,
    removingReferences,
    updateDataObject,
    updateOrCreateItemFromDataObject,
    deleteItemFromDataObject,
    findObjectFieldBySuffixName,
    findObjectField,
    findObjectName,
    findObjectProductNumber,
    findObjectRecordNumber,
    findObjectCASNumber,
    findObjectEGNumber,
    getCanonicalDocumentName,
    updateDataObjectMetadata,
    formatMaterialActivityLog,
    formatSapActivityLog,
    getMaterialActivityFieldsToShowByAction,
    filterOutMetadataItems,
    sortItems,
    addHazardInducerReference,
    removeHazardInducerReference,
    addDataObjectReferences,
    removeDataObjectReferences,
    getDefaultReferenceSearchTagsByReferenceType,
    syncZeroSectionTrackingItems,
    formatReferenceMetadata,
  }
}
